import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { RefreshCw, Wand2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  ClassifiedCompany,
  CompanyTypeOption,
  listClassifiedCompanies,
  reclassifyCompany,
} from "@/services/companyClassificationApi";

import { ALL, COMPANY_TYPE_SOURCE_LABELS, companySourceLabel, companyTypeLabel, NONE } from "./classification";
import { OverrideDialog, OverrideDraft } from "./OverrideDialog";

const PAGE_SIZE = 25;

export function CompaniesTab({ types }: { types: CompanyTypeOption[] }) {
  const navigate = useNavigate();
  const [items, setItems] = useState<ClassifiedCompany[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState(ALL);
  const [sourceFilter, setSourceFilter] = useState(ALL);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [draft, setDraft] = useState<OverrideDraft | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listClassifiedCompanies(navigate, {
        q: search.trim(),
        type: typeFilter === ALL ? undefined : typeFilter === NONE ? "none" : typeFilter,
        source: sourceFilter === ALL ? undefined : sourceFilter === NONE ? "none" : sourceFilter,
        page,
        size: PAGE_SIZE,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur inconnue");
    } finally {
      setLoading(false);
    }
  }, [navigate, search, typeFilter, sourceFilter, page]);

  useEffect(() => {
    const timer = setTimeout(refresh, 250);
    return () => clearTimeout(timer);
  }, [refresh]);

  const handleReclassify = async (company: ClassifiedCompany) => {
    try {
      await reclassifyCompany(company.id, navigate);
      setNotice(`« ${company.name} » sera reclassée sous une minute.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur inconnue");
    }
  };

  const forceType = (company: ClassifiedCompany) =>
    setDraft({
      name: company.name,
      linkedin_company_ids: company.linkedin_company_id ? [company.linkedin_company_id] : [],
      company_type: company.company_type && company.company_type !== "other" ? company.company_type : undefined,
    });

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          className="h-9 w-64"
          placeholder="Rechercher un nom ou un ID"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
        <Select
          value={typeFilter}
          onValueChange={(value) => {
            setTypeFilter(value);
            setPage(1);
          }}
        >
          <SelectTrigger className="h-9 w-60">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Tous les types</SelectItem>
            <SelectItem value={NONE}>Non classées</SelectItem>
            {types.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={sourceFilter}
          onValueChange={(value) => {
            setSourceFilter(value);
            setPage(1);
          }}
        >
          <SelectTrigger className="h-9 w-52">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Toutes les sources</SelectItem>
            <SelectItem value={NONE}>Sans source</SelectItem>
            {Object.entries(COMPANY_TYPE_SOURCE_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={refresh}>
          <RefreshCw className="mr-1 h-3 w-3" /> Rafraîchir
        </Button>
        <span className="ml-auto text-xs text-muted-foreground">
          {loading ? "Chargement…" : `${total.toLocaleString("fr-FR")} entreprises`}
        </span>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
      {notice && <p className="text-sm text-muted-foreground">{notice}</p>}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Entreprise</TableHead>
            <TableHead>ID LinkedIn</TableHead>
            <TableHead>Industries</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Source</TableHead>
            <TableHead className="w-64" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {!loading && items.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} className="text-center text-sm text-muted-foreground">
                Aucune entreprise
              </TableCell>
            </TableRow>
          )}
          {items.map((company) => (
            <TableRow key={company.id}>
              <TableCell className="font-medium">
                <Link to={`/companies/${company.id}`} className="hover:underline">
                  {company.name}
                </Link>
                {company.override_id && (
                  <Badge variant="outline" className="ml-2">
                    correction #{company.override_id}
                  </Badge>
                )}
              </TableCell>
              <TableCell className="text-xs text-muted-foreground">{company.linkedin_company_id ?? "—"}</TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {company.industries.map((industry) => industry.name).join(", ") || "—"}
              </TableCell>
              <TableCell>
                <Badge variant={company.company_type ? "secondary" : "outline"}>
                  {companyTypeLabel(company.company_type)}
                </Badge>
              </TableCell>
              <TableCell className="text-sm">{companySourceLabel(company.company_type_source)}</TableCell>
              <TableCell>
                <div className="flex justify-end gap-1">
                  <Button variant="outline" size="sm" onClick={() => forceType(company)}>
                    <Wand2 className="mr-1 h-3 w-3" /> Forcer le type
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleReclassify(company)}>
                    Reclasser
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <div className="flex items-center justify-end gap-2 text-sm">
        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
          Précédent
        </Button>
        <span>
          Page {page} / {pages}
        </span>
        <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
          Suivant
        </Button>
      </div>

      <OverrideDialog
        open={draft !== null}
        onOpenChange={(open) => !open && setDraft(null)}
        types={types}
        draft={draft}
        onSaved={() => {
          setNotice("Correction enregistrée — reclassement sous une minute.");
          refresh();
        }}
      />
    </div>
  );
}
